/**
 * Types mirroring the backend Games API contract.
 * Keep in sync with GamesApiController and GamesConstants.java.
 */

export type GameType =
  | 'SPIN_WHEEL'
  | 'SCRATCH_CARD'
  | 'SLOT_MACHINE'
  | 'MYSTERY_BOX'
  | 'DICE_ROLL'
  | 'GUESS_NUMBER'
  | 'STOP_CLOCK'
  | 'BILL_SPLIT'
  | 'GOOGLE_REVIEW';

export interface GameSegment {
  label: string;
}

export interface GameEligibility {
  eligible: boolean;
  gameType: GameType;
  segments?: GameSegment[];
  reason?: 'ALREADY_PLAYED_TODAY' | 'REVIEW_ALREADY_CLAIMED' | 'GAME_INACTIVE' | 'GOOGLE_URL_NOT_CONFIGURED';
  nextEligibleAt?: string;
  todayResult?: GameTodayResult;
  googleReviewUrl?: string;
  rewardLabel?: string;
  discountType?: string;
  discountValue?: string;
}

export interface GameTodayResult {
  gameType?: GameType;
  resultSegment: string;
  redemptionCode?: string | null;
  expiresAt?: string | null;
  discountType?: string;
  discountValue?: string;
  isWin?: boolean;
  redeemed?: boolean;
  playedAt?: string;
  userReviewLink?: string;
}

export interface SpinResult {
  resultSegment: string;
  redemptionCode: string | null;
  expiresAt: string | null;
}

export interface StopClockStartResponse {
  sessionToken: string;
  pingRequired: boolean;
}

export interface StopClockPingResponse {
  serverReceivedAt: number;
}

export interface StopClockStopResponse {
  success: boolean;
  precisionMs: number;
  redemptionCode?: string;
}

export interface BillSplitResponse {
  chosenName: string;
  rerollsRemaining: number;
}

export interface RedeemResponse {
  status: 'REDEEMED';
  discountApplied: {
    type: 'PERCENT' | 'FLAT' | 'FREE_ITEM' | 'BOGO' | 'NONE';
    value: string | number;
  };
}

export interface GameMeta {
  gameType: GameType;
  label: string;
  emoji: string;
  description: string;
}

export const ALL_GAMES: GameMeta[] = [
  { gameType: 'SPIN_WHEEL', label: 'Spin the Wheel', emoji: '🎡', description: 'Spin for a chance to win a discount' },
  { gameType: 'SCRATCH_CARD', label: 'Scratch Card', emoji: '🃏', description: 'Scratch and reveal your reward' },
  { gameType: 'SLOT_MACHINE', label: 'Slot Machine', emoji: '🎰', description: 'Match 3 symbols to win big' },
  { gameType: 'MYSTERY_BOX', label: 'Mystery Box', emoji: '🎁', description: 'Pick a box and discover your prize' },
  { gameType: 'DICE_ROLL', label: 'Dice Roll', emoji: '🎲', description: 'Roll a 6 for a free add-on' },
  { gameType: 'GUESS_NUMBER', label: "Chef's Number", emoji: '🔢', description: 'Guess the number closest to win' },
  { gameType: 'STOP_CLOCK', label: 'Stop the Clock', emoji: '⏱️', description: 'Tap at the perfect moment to win' },
  { gameType: 'BILL_SPLIT', label: 'Bill Split', emoji: '🪙', description: 'Spin to decide who picks up the tab' },
  { gameType: 'GOOGLE_REVIEW', label: 'Google Review', emoji: '⭐', description: 'Leave a review and claim your reward' },
];
